import {
  type CountryCode,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from "libphonenumber-js/min";

export type PhoneCountryOption = {
  country: CountryCode;
  callingCode: string;
  label: string;
  flag: string;
};

const countryNames = new Intl.DisplayNames(["en"], { type: "region" });

const toFlagEmoji = (country: CountryCode) =>
  country
    .toUpperCase()
    .replace(/./g, (char) =>
      String.fromCodePoint(127_397 + char.charCodeAt(0)),
    );

const buildPhoneCountryOption = (country: CountryCode): PhoneCountryOption => ({
  country,
  callingCode: getCountryCallingCode(country),
  label: countryNames.of(country) ?? country,
  flag: toFlagEmoji(country),
});

const prioritizeCountry = (country: CountryCode) => (country === "IN" ? 0 : 1);

export const PHONE_COUNTRY_OPTIONS = getCountries()
  .map(buildPhoneCountryOption)
  .sort((left, right) => {
    const priorityDiff =
      prioritizeCountry(left.country) - prioritizeCountry(right.country);
    if (priorityDiff !== 0) return priorityDiff;
    return left.label.localeCompare(right.label);
  });

export type SupportedPhoneCountry = (typeof PHONE_COUNTRY_OPTIONS)[number];

export const DEFAULT_PHONE_COUNTRY = PHONE_COUNTRY_OPTIONS[0];

export const normalizePhoneInput = (value: string) =>
  value.replace(/[^\d+\s()-]/g, "").slice(0, 24);

export const getPhoneCountryOption = (country: CountryCode) =>
  PHONE_COUNTRY_OPTIONS.find((option) => option.country === country) ??
  DEFAULT_PHONE_COUNTRY;

export const getPhoneCountryOptionFromValue = (country: string) =>
  PHONE_COUNTRY_OPTIONS.find((option) => option.country === country) ??
  DEFAULT_PHONE_COUNTRY;

export const normalizePhoneNumberForCountries = (
  value: string,
  countries: readonly PhoneCountryOption[],
  defaultCountry: PhoneCountryOption = countries[0] ?? DEFAULT_PHONE_COUNTRY,
) => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (countries.length === 0) return null;

  const phoneNumber = parsePhoneNumberFromString(
    trimmed,
    defaultCountry.country,
  );
  if (!phoneNumber?.isValid()) return null;

  const matchedCountry = countries.find(
    (country) =>
      phoneNumber.country === country.country &&
      phoneNumber.countryCallingCode === country.callingCode,
  );
  if (!matchedCountry) return null;

  return phoneNumber.number;
};

export const normalizePaymentPhoneNumber = (
  value: string,
  defaultCountry: PhoneCountryOption = DEFAULT_PHONE_COUNTRY,
) =>
  normalizePhoneNumberForCountries(
    value,
    PHONE_COUNTRY_OPTIONS,
    defaultCountry,
  );

export const getPhoneCountryForNumber = (value: string) => {
  const phoneNumber = parsePhoneNumberFromString(value.trim());
  return phoneNumber?.country
    ? getPhoneCountryOption(phoneNumber.country)
    : null;
};

/**
 * The product is India-only, so a number with no country code is read as
 * Indian. Every stored phone goes through `normalizePaymentPhoneNumber`
 * (E.164); every shown phone goes through `formatPhoneForDisplay`.
 */
const DISPLAY_DEFAULT_COUNTRY: CountryCode = "IN";

/**
 * One human format for every phone surface, CSV exports included:
 * "+91 98201 40079". The spaces are deliberate — a spreadsheet keeps a spaced
 * value as text, where a bare "+919820140079" becomes a number and loses its +.
 *
 * A value that does not parse as a valid number comes back trimmed but
 * otherwise untouched, so legacy junk is shown rather than silently dropped.
 */
export const formatPhoneForDisplay = (
  value: string | null | undefined,
): string | null => {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) return null;

  const phoneNumber = parsePhoneNumberFromString(
    trimmed,
    DISPLAY_DEFAULT_COUNTRY,
  );
  if (!phoneNumber?.isValid()) return trimmed;

  return phoneNumber.formatInternational();
};

/** Max digits the fixed-+91 input accepts: an Indian national number. */
export const INDIA_NATIONAL_NUMBER_LENGTH = 10;

/**
 * The national digits a fixed-+91 input should show for a value in any
 * format: "+919820140079", "919820140079", "09820140079" and
 * "+91 98201 40079" all give "9820140079".
 *
 * A valid number from another country keeps its calling code in the digits
 * ("14155552671") rather than being trimmed into a different Indian-looking
 * number, so the server rejects it instead of storing a stranger's line.
 */
export const toNationalDigits = (value: string | null | undefined): string => {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) return "";

  const phoneNumber = parsePhoneNumberFromString(
    trimmed,
    DISPLAY_DEFAULT_COUNTRY,
  );
  if (phoneNumber?.isValid()) {
    return phoneNumber.country === DISPLAY_DEFAULT_COUNTRY
      ? phoneNumber.nationalNumber
      : `${phoneNumber.countryCallingCode}${phoneNumber.nationalNumber}`;
  }

  const digits = trimmed.replace(/\D/g, "");
  // Partial input while typing or pasting: drop a +91 / 0 trunk prefix the
  // fixed chip already stands for.
  if (trimmed.startsWith("+91")) return digits.slice(2);
  if (digits.length > INDIA_NATIONAL_NUMBER_LENGTH && digits.startsWith("0")) {
    return digits.replace(/^0+/, "");
  }
  return digits;
};

/**
 * Digits only, country code included — what wa.me and similar deep links
 * want. A bare Indian national number gets its 91.
 */
export const toInternationalDigits = (
  value: string | null | undefined,
): string | null => {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) return null;

  const canonical = normalizePaymentPhoneNumber(trimmed);
  if (canonical) return canonical.slice(1);

  const digits = trimmed.replace(/\D/g, "");
  return digits.length > 0 ? digits : null;
};

/** `tel:` href that dials from anywhere — E.164 when the value parses. */
export const phoneTelHref = (value: string | null | undefined) => {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) return null;
  const canonical = normalizePaymentPhoneNumber(trimmed);
  return `tel:${canonical ?? trimmed.replace(/[^\d+]/g, "")}`;
};
